import { useState, useEffect } from 'react';
import { logError } from 'setup/logger/logError';
import * as XLSX from 'xlsx';
import { fileKindService } from 'services/fileService';
import { PreviewSecurityOptions } from '../../app/constants/previewSecurity';
import {
  DEFAULT_PREVIEW_PARSE_TIMEOUT_MS,
  MAX_PREVIEW_FILE_SIZE_BYTES,
} from '../../app/constants/filePreview';

interface UseExcelDocumentResult {
  loading: boolean;
  error: boolean;
  workbook: XLSX.WorkBook | null;
}

export const useExcelDocument = (
  src: string | undefined,
  _options?: PreviewSecurityOptions
): UseExcelDocumentResult => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);

  useEffect(() => {
    if (!src) {
      setError(true);
      setLoading(false);
      return;
    }

    const loadExcel = async (): Promise<void> => {
      const timeoutId = setTimeout(() => {
        logError('Excel file parsing timeout');
        setError(true);
        setLoading(false);
      }, DEFAULT_PREVIEW_PARSE_TIMEOUT_MS);

      try {
        setLoading(true);
        setError(false);

        // Fetch the Excel file
        const response = await fetch(src, { credentials: 'include' });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Validate file size from Content-Length header (before downloading)
        const contentLength = response.headers.get('content-length');
        if (contentLength) {
          const fileSize = parseInt(contentLength, 10);
          if (fileSize > MAX_PREVIEW_FILE_SIZE_BYTES) {
            // File too large - this is not an error, just a limitation
            clearTimeout(timeoutId);
            setError(true);
            setLoading(false);
            return;
          }
        }

        // Validate Content-Type
        const contentType = response.headers.get('content-type');
        if (
          contentType &&
          !fileKindService.isExcelContentType(contentType)
        ) {
          logError('Invalid content type for Excel file:', contentType);
          throw new Error('Invalid file type');
        }

        const arrayBuffer = await response.arrayBuffer();

        // Double-check file size after download (in case Content-Length was missing)
        if (arrayBuffer.byteLength > MAX_PREVIEW_FILE_SIZE_BYTES) {
          // File too large - this is not an error, just a limitation
          clearTimeout(timeoutId);
          setError(true);
          setLoading(false);
          return;
        }

        // Parse Excel file using xlsx
        const parsedWorkbook = XLSX.read(arrayBuffer, { type: 'array' });

        clearTimeout(timeoutId);
        setWorkbook(parsedWorkbook);
        setLoading(false);
      } catch (err) {
        clearTimeout(timeoutId);
        logError('Error loading Excel file:', err);
        logError('Source URL:', src);
        setError(true);
        setLoading(false);
      }
    };

    loadExcel();
  }, [src]);

  return { loading, error, workbook };
};
